import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, getDay, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CheckCircle, XCircle, HourglassSimple, Check, X, Clock, MapPin, PencilSimple, Trash, User, CalendarBlank, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useSearch } from '../components/VerticalTabs';
import { AdminPunchButton } from '../components/time-entries/AdminPunchModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeeAvatar = ({ photoUrl, name, size = 36 }) => {
  const [errored, setErrored] = useState(false);
  const initial = (name || '?').charAt(0).toUpperCase();
  if (photoUrl && !errored) {
    return (
      <img
        src={`${API_URL}${photoUrl}`}
        alt={name}
        onError={() => setErrored(true)}
        style={{ width: `${size}px`, height: `${size}px`, borderRadius: '50%', objectFit: 'cover', border: '2px solid white', boxShadow: '0 0 0 1px #E2E2E6' }}
      />
    );
  }
  return (
    <div style={{ width: `${size}px`, height: `${size}px`, borderRadius: '50%', background: 'linear-gradient(135deg, #002FA7, #3D5AFE)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: `${Math.max(12, size / 2.5)}px`, border: '2px solid white', boxShadow: '0 0 0 1px #E2E2E6' }}>
      {initial}
    </div>
  );
};

const EditEntryModal = ({ entry, projects, onClose, onSaved }) => {
  const [form, setForm] = useState({
    start_time: entry.start_time || '',
    end_time: entry.end_time || '',
    project_id: entry.project_id || '',
    location_address: entry.location_address || '',
    break_duration: entry.break_duration || 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        start_time: form.start_time,
        end_time: form.end_time,
        break_duration: Number(form.break_duration) || 0,
        project_id: form.project_id || null,
        location_address: form.location_address,
      };
      await axios.put(`${API_URL}/api/time-entries/${entry.id}`, payload, { withCredentials: true });
      toast.success('Pointage modifie');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '10px', width: '100%', maxWidth: '480px', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Modifier le pointage</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
        </div>
        <p style={{ fontSize: '0.82rem', color: '#5E5F66', margin: '0 0 16px 0' }}>
          {entry.user_name} - {format(parseISO(entry.date), 'EEEE dd MMMM yyyy', { locale: fr })}
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Debut</label>
              <input data-testid="edit-entry-start" type="time" className="form-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Fin</label>
              <input data-testid="edit-entry-end" type="time" className="form-input" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Pause (min)</label>
            <input type="number" min="0" step="1" className="form-input" value={form.break_duration} onChange={(e) => setForm({ ...form, break_duration: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Chantier</label>
            <select data-testid="edit-entry-project" className="form-input" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
              <option value="">-- Aucun --</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}{p.client_name ? ` - ${p.client_name}` : ''}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Localisation</label>
            <input data-testid="edit-entry-location" type="text" className="form-input" value={form.location_address} onChange={(e) => setForm({ ...form, location_address: e.target.value })} placeholder="ex: 10 rue des Pointeurs, Roubaix" />
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '14px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="edit-entry-save">{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MonthCalendarView = ({ allEntries, onSelectDate, selectedDate }) => {
  // Year cursor (0..11 for displayed reference month is no longer used —
  // we render the full year). Default = current year.
  const [yearCursor, setYearCursor] = useState(() => {
    const base = selectedDate || new Date();
    return base.getFullYear();
  });

  const entriesByDay = useMemo(() => {
    const map = {};
    (allEntries || []).forEach(e => {
      if (!e.date) return;
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    });
    return map;
  }, [allEntries]);

  const renderMonth = (monthIdx) => {
    const monthRef = new Date(yearCursor, monthIdx, 1);
    const monthStart = startOfMonth(monthRef);
    const monthEnd = endOfMonth(monthRef);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const leadingBlanks = (getDay(monthStart) + 6) % 7; // Mon-first
    const weekdays = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
    return (
      <div key={monthIdx} data-testid={`cal-month-${yearCursor}-${monthIdx + 1}`}
        style={{
          minWidth: 0,
          border: '1.5px solid #C5C7CD',
          borderRadius: 8,
          padding: '8px 10px',
          background: '#FFFFFF',
        }}>
        <div style={{
          fontWeight: 700,
          fontSize: '0.78rem',
          textTransform: 'uppercase',
          color: 'var(--primary, #002FA7)',
          marginBottom: 6,
          textAlign: 'center',
          letterSpacing: '0.04em',
          paddingBottom: 4,
          borderBottom: '1px solid #E2E2E6',
        }}>
          {format(monthRef, 'MMMM', { locale: fr })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {weekdays.map((d, i) => (
            <div key={`wd-${monthIdx}-${i}`} style={{ fontSize: '0.62rem', fontWeight: 700, textAlign: 'center', color: '#9E9EA7' }}>{d}</div>
          ))}
          {Array.from({ length: leadingBlanks }).map((_, i) => (<div key={`b-${monthIdx}-${i}`} />))}
          {days.map(day => {
            const iso = format(day, 'yyyy-MM-dd');
            const dayEntries = entriesByDay[iso] || [];
            const count = dayEntries.length;
            const pendingCount = dayEntries.filter(e => e.status === 'pending').length;
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const today = isToday(day);
            return (
              <button
                type="button"
                key={iso}
                data-testid={`cal-day-${iso}`}
                onClick={() => onSelectDate(day)}
                title={count > 0 ? `${count} pointage(s)${pendingCount ? ` - ${pendingCount} en attente` : ''}` : ''}
                style={{
                  position: 'relative',
                  aspectRatio: '1 / 1',
                  border: 'none',
                  background: isSelected ? 'var(--primary, #002FA7)' : (count > 0 ? '#E3F2FD' : 'transparent'),
                  color: isSelected ? 'white' : (today ? 'var(--primary, #002FA7)' : '#1F2024'),
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '0.72rem',
                  fontWeight: today || isSelected ? 700 : 500,
                  padding: 0,
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  outline: today && !isSelected ? '1.5px solid var(--primary, #002FA7)' : 'none',
                  outlineOffset: '-2px',
                  transition: 'background-color 0.12s',
                }}
              >
                {format(day, 'd')}
                {count > 0 && !isSelected && (
                  <span style={{
                    position: 'absolute',
                    bottom: 1,
                    right: 1,
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: pendingCount > 0 ? '#E65100' : '#1565C0',
                  }} />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const months = Array.from({ length: 12 }, (_, i) => i);

  return (
    <div data-testid="validate-calendar-view">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8 }}>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setYearCursor(y => y - 1)}
          data-testid="cal-prev-year"
          style={{ padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <CaretLeft size={14} weight="bold" /> Annee precedente
        </button>
        <div style={{ fontWeight: 800, fontSize: '1.2rem' }} data-testid="cal-current-year">
          {yearCursor}
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setYearCursor(y => y + 1)}
          data-testid="cal-next-year"
          style={{ padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          Annee suivante <CaretRight size={14} weight="bold" />
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
        gap: '12px',
      }}>
        {months.map(renderMonth)}
      </div>

      <div style={{ display: 'flex', gap: 14, fontSize: '0.72rem', color: '#5E5F66', marginTop: 12, flexWrap: 'wrap' }}>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#1565C0', marginRight: 4 }} /> pointages enregistres</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#E65100', marginRight: 4 }} /> en attente de validation</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--primary, #002FA7)', marginRight: 4 }} /> jour selectionne / aujourd'hui</span>
      </div>
    </div>
  );
};

const formatDuration = (h) => {
  if (!h && h !== 0) return '-';
  const total = Math.round(h * 100) / 100;
  return `${total}h`;
};

const DayEntriesPanel = ({ date, entries, getStatusBadge, onEdit }) => {
  if (!date) {
    return (
      <div className="empty-state" data-testid="cal-no-day-selected">
        <CalendarBlank size={48} className="empty-state-icon" />
        <p className="empty-state-text">Cliquez sur une date pour voir les pointages</p>
      </div>
    );
  }
  // Group by project for clarity
  const grouped = entries.reduce((acc, e) => {
    const key = e.project_id || '__none__';
    if (!acc[key]) acc[key] = { project_name: e.project_name || 'Sans chantier', items: [] };
    acc[key].items.push(e);
    return acc;
  }, {});
  const groups = Object.values(grouped);

  return (
    <div className="card" style={{ padding: '16px', marginTop: '16px' }} data-testid="cal-day-panel">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', textTransform: 'capitalize' }}>
          {format(date, 'EEEE dd MMMM yyyy', { locale: fr })}
        </h3>
        <span style={{ fontSize: '0.82rem', color: '#5E5F66' }}>
          {entries.length} pointage{entries.length > 1 ? 's' : ''}
        </span>
      </div>
      {entries.length === 0 ? (
        <p style={{ color: '#5E5F66', fontSize: '0.9rem', margin: 0 }}>Aucun pointage ce jour.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {groups.map((g, idx) => (
            <div key={idx} data-testid={`cal-day-project-${idx}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ padding: '3px 10px', borderRadius: '6px', background: '#E3F2FD', color: '#1565C0', fontWeight: 700, fontSize: '0.82rem' }}>
                  {g.project_name}
                </span>
                <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{g.items.length} salarié{g.items.length > 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {g.items.map(e => (
                  <div
                    key={e.id}
                    data-testid={`cal-day-entry-${e.id}`}
                    onClick={onEdit ? () => onEdit(e) : undefined}
                    role={onEdit ? 'button' : undefined}
                    tabIndex={onEdit ? 0 : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 10px',
                      border: '1px solid #E2E2E6',
                      borderRadius: '8px',
                      background: 'white',
                      flexWrap: 'wrap',
                      cursor: onEdit ? 'pointer' : 'default',
                      transition: 'background-color 0.12s, border-color 0.12s',
                    }}
                    onMouseEnter={onEdit ? (ev) => { ev.currentTarget.style.backgroundColor = '#F4F8FF'; ev.currentTarget.style.borderColor = '#90CAF9'; } : undefined}
                    onMouseLeave={onEdit ? (ev) => { ev.currentTarget.style.backgroundColor = 'white'; ev.currentTarget.style.borderColor = '#E2E2E6'; } : undefined}
                    title={onEdit ? 'Cliquer pour modifier ce pointage' : undefined}
                  >
                    <EmployeeAvatar photoUrl={e.user_photo_url} name={e.user_name} size={32} />
                    <div style={{ flex: 1, minWidth: '160px' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{e.user_name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>{e.user_email}</div>
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#1F2024', minWidth: '110px' }}>
                      <Clock size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                      {e.start_time || '--:--'} → {e.end_time || '--:--'}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', minWidth: '56px', textAlign: 'right' }}>
                      {formatDuration(e.total_hours)}
                    </div>
                    <div>{getStatusBadge(e.status)}</div>
                    {onEdit && (
                      <PencilSimple
                        size={14}
                        weight="bold"
                        color="#1565C0"
                        style={{ marginLeft: 4 }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ValidateHoursPage = ({ embedded }) => {
  const [entries, setEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('pending');
  const [editing, setEditing] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  const fetchEntries = useCallback(async () => {
    try {
      const [pendingRes, allRes, projRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/api/time-entries/pending`, { withCredentials: true }),
        axios.get(`${API_URL}/api/time-entries`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users/employees`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setEntries(pendingRes.data);
      setAllEntries(allRes.data);
      setProjects(projRes.data || []);
      setEmployees(empRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleValidate = async (entryId) => {
    try {
      await axios.post(`${API_URL}/api/time-entries/${entryId}/validate`, {}, { withCredentials: true });
      toast.success('Entrée validée');
      fetchEntries();
    } catch { toast.error('Erreur lors de la validation'); }
  };

  const handleReject = async (entryId) => {
    try {
      await axios.post(`${API_URL}/api/time-entries/${entryId}/reject`, {}, { withCredentials: true });
      toast.success('Entrée rejetée');
      fetchEntries();
    } catch { toast.error('Erreur lors du rejet'); }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Supprimer le pointage de ${entry.user_name} du ${entry.date} ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/time-entries/${entry.id}`, { withCredentials: true });
      toast.success('Pointage supprime');
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <span className="status-badge approved"><CheckCircle size={14} /> Validé</span>;
      case 'rejected': return <span className="status-badge rejected"><XCircle size={14} /> Rejeté</span>;
      default: return <span className="status-badge pending"><HourglassSimple size={14} /> En attente</span>;
    }
  };

  const { searchTerm } = useSearch();

  const displayEntries = (view === 'pending' ? entries : allEntries).filter(e => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (e.user_name || '').toLowerCase().includes(s) || (e.project_name || '').toLowerCase().includes(s);
  });

  // Group entries by date (most recent day first). Within a day, sort by start_time desc.
  const groupedByDate = (() => {
    const byDate = {};
    displayEntries.forEach((e) => {
      const k = e.date || 'sans_date';
      (byDate[k] = byDate[k] || []).push(e);
    });
    const sortedDates = Object.keys(byDate).sort((a, b) => (a === 'sans_date' ? 1 : b === 'sans_date' ? -1 : b.localeCompare(a)));
    return sortedDates.map((date) => ({
      date,
      items: byDate[date].sort((x, y) => (y.start_time || '').localeCompare(x.start_time || '')),
    }));
  })();

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="validate-hours-page">
      {!embedded && (
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 className="page-title">Valider les heures</h1>
            <p className="page-subtitle">Approuvez, modifiez ou supprimez les saisies des employes</p>
          </div>
          <AdminPunchButton employees={employees} projects={projects} onCreated={fetchEntries} />
        </div>
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <AdminPunchButton employees={employees} projects={projects} onCreated={fetchEntries} />
        </div>
      )}

      <div className="tabs">
        <button className={`tab ${view === 'pending' ? 'active' : ''}`} onClick={() => setView('pending')} data-testid="view-pending">
          En attente ({entries.length})
        </button>
        <button className={`tab ${view === 'all' ? 'active' : ''}`} onClick={() => setView('all')} data-testid="view-all">
          Toutes les entrées ({allEntries.length})
        </button>
        <button className={`tab ${view === 'calendar' ? 'active' : ''}`} onClick={() => setView('calendar')} data-testid="view-calendar" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <CalendarBlank size={14} weight="bold" />
          <span>Calendrier</span>
        </button>
      </div>

      {view === 'calendar' ? (
        <div data-testid="validate-calendar-wrapper">
          <MonthCalendarView
            allEntries={allEntries}
            onSelectDate={(d) => setSelectedDate(d)}
            selectedDate={selectedDate}
          />
          <DayEntriesPanel
            date={selectedDate}
            entries={selectedDate ? allEntries.filter(e => e.date === format(selectedDate, 'yyyy-MM-dd')) : []}
            getStatusBadge={getStatusBadge}
            onEdit={(entry) => setEditing(entry)}
          />
        </div>
      ) : displayEntries.length > 0 ? (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}></th>
                <th>Employé</th>
                <th>Date</th>
                <th>Début</th>
                <th>Fin</th>
                <th>Total</th>
                <th>Chantier</th>
                <th>Localisation</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groupedByDate.map((group) => (
                <React.Fragment key={group.date}>
                  <tr data-testid={`validate-date-header-${group.date}`} className="date-group-header">
                    <td colSpan={10} style={{
                      background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)',
                      borderTop: '2px solid #1D4ED8',
                      padding: '8px 14px',
                      fontWeight: 700,
                      fontSize: '0.86rem',
                      color: '#1E3A8A',
                      textTransform: 'capitalize',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <CalendarBlank size={14} weight="bold" />
                        {group.date === 'sans_date'
                          ? 'Sans date'
                          : format(parseISO(group.date), 'EEEE dd MMMM yyyy', { locale: fr })}
                        <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 999, background: '#1D4ED8', color: 'white', fontSize: '0.72rem' }}>
                          {group.items.length} pointage{group.items.length > 1 ? 's' : ''}
                        </span>
                      </span>
                    </td>
                  </tr>
                  {group.items.map((entry) => (
                    <tr key={entry.id} data-testid={`validate-row-${entry.id}`}>
                      <td>
                        <EmployeeAvatar photoUrl={entry.user_photo_url} name={entry.user_name} />
                      </td>
                      <td>
                        <div style={{ fontWeight: '600' }}>{entry.user_name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>{entry.user_email}</div>
                      </td>
                      <td>{format(parseISO(entry.date), 'EEEE dd/MM', { locale: fr })}</td>
                      <td>{entry.start_time}</td>
                      <td>{entry.end_time}</td>
                      <td style={{ fontWeight: '600' }}>{Math.round(entry.total_hours * 100) / 100}h</td>
                      <td style={{ fontSize: '0.82rem' }}>
                        {entry.project_name ? (
                          <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#E3F2FD', color: '#1565C0', fontWeight: '600' }}>{entry.project_name}</span>
                        ) : <span style={{ color: '#9A9AA0' }}>-</span>}
                      </td>
                      <td>
                        {entry.latitude && entry.longitude ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <MapPin size={16} color="#00C853" weight="fill" />
                            <a href={`https://www.google.com/maps?q=${entry.latitude},${entry.longitude}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: '#002FA7' }} title={entry.location_address || 'Voir sur la carte'}>
                              {entry.location_address ? entry.location_address.slice(0, 18) + (entry.location_address.length > 18 ? '...' : '') : 'Voir'}
                            </a>
                          </div>
                        ) : entry.location_address ? (
                          <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{entry.location_address.slice(0, 24)}</span>
                        ) : <span style={{ fontSize: '0.75rem', color: '#9A9AA0' }}>-</span>}
                      </td>
                      <td>{getStatusBadge(entry.status)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap' }}>
                          {view === 'pending' && (
                            <>
                              <button data-testid={`approve-entry-${entry.id}`} className="btn btn-success btn-sm btn-icon" onClick={() => handleValidate(entry.id)} title="Valider"><Check size={14} weight="bold" /></button>
                              <button data-testid={`reject-entry-${entry.id}`} className="btn btn-secondary btn-sm btn-icon" onClick={() => handleReject(entry.id)} title="Rejeter"><X size={14} /></button>
                            </>
                          )}
                          <button data-testid={`edit-entry-${entry.id}`} className="btn btn-secondary btn-sm btn-icon" onClick={() => setEditing(entry)} title="Modifier"><PencilSimple size={14} /></button>
                          <button data-testid={`delete-entry-${entry.id}`} className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(entry)} title="Supprimer"><Trash size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Clock size={48} className="empty-state-icon" />
          <p className="empty-state-text">
            {view === 'pending' ? 'Aucune entrée en attente' : 'Aucune entrée trouvée'}
          </p>
        </div>
      )}

      {editing && (
        <EditEntryModal
          entry={editing}
          projects={projects}
          onClose={() => setEditing(null)}
          onSaved={fetchEntries}
        />
      )}
    </div>
  );
};

export default ValidateHoursPage;
